import {
  ariaAttributes,
  setTabIndexes,
  accessibleSubMenus,
} from '../helpers/accessibility';

import { debounce } from 'lodash';

import { isMobileOrTablet } from '../helpers/layout';
import { fireAdobeAnalyticsEvent } from '../helpers/adobe-analytics';

const navSelectedEvent = new Event('nav-item-selected', {
    bubbles: true,
    composed: true,
  }),
  resetMobileNav = new Event('reset-mobile-nav', {
    bubbles: true,
    composed: true,
  }),
  showClass = 'show',
  dimmedClass = 'dimmed',
  lockScrollClass = 'lock-scroll',
  headerPlacement = 'Top Nav Header',
  secondaryPlacement = 'Top Nav Secondary';

let main,
  globalNav,
  footer,
  navPanel,
  globalSearch,
  globalSearchTrigger,
  parentListItems,
  mobileNavToggle,
  topNavElement;

// Publish the top-nav's live bottom-edge as a CSS variable so main and
// nav-panel can position under it without JS setting inline styles on
// every toggle.
const setTopNavHeightVar = () => {
  const bottom = topNavElement?.getBoundingClientRect().bottom;
  if (bottom > 0) {
    document.documentElement.style.setProperty(
      '--top-nav-height',
      `${bottom}px`
    );
  }
};

// A pointerdown anywhere outside these closes whichever panel is open.
// Parent list items are included so switching sections on desktop doesn't
// race with the outside-click handler.
const KEEP_OPEN_SELECTOR =
  'nav-panel, global-search, .js-toggle-mobile-nav, #js-new-nav-search-button, .js-parent-list-item';

const init = function() {
  main = document.querySelector('main');
  globalNav = document.getElementById('js-global-nav');
  footer = document.querySelector('global-footer');
  navPanel = document.querySelector('nav-panel');
  globalSearch = document.querySelector('global-search');
  mobileNavToggle = document.querySelector('.js-toggle-mobile-nav');
  globalSearchTrigger = document.querySelector('#js-new-nav-search-button');
  parentListItems = Array.from(
    document.querySelectorAll('.js-parent-list-item')
  );
  topNavElement = document.getElementById('js-main-nav');

  mobileNavToggle.addEventListener('click', () => {
    if (!navPanel.classList.contains(showClass)) {
      navPanel.dispatchEvent(resetMobileNav);
    }
    toggleNavPanelDisplay();
    if (navPanel.classList.contains(showClass)) sendInpEvent('Open Menu');
  });

  globalSearchTrigger.addEventListener('click', event => {
    toggleSearchPanelDisplay(event);
    // Below desktop the search opens inside the nav panel.
    if (
      globalSearch.classList.contains(showClass) ||
      navPanel.classList.contains(showClass)
    ) {
      sendInpEvent('Open Search');
    }
  });
  globalSearch.addEventListener('close-search', () => {
    if (globalSearch.classList.contains(showClass)) toggleSearchPanelDisplay();
  });

  setTopNavHeightVar();
  window.addEventListener('resize', debounce(setTopNavHeightVar, 60));
  if (globalNav) {
    ariaAttributes();
    accessibleSubMenus();
    parentListItemEventListeners();
    requestQuoteEventListener();
    setupAnalyticsListener();
  }
  document.addEventListener('pointerdown', handleOutsideClick);
};

const handleOutsideClick = function(event) {
  const navOpen = navPanel.classList.contains(showClass);
  const searchOpen = globalSearch.classList.contains(showClass);
  if (!navOpen && !searchOpen) return;
  if (event.target.closest(KEEP_OPEN_SELECTOR)) return;
  if (navOpen) toggleNavPanelDisplay();
  else toggleSearchPanelDisplay();
};

const requestQuoteEventListener = function() {
  const requestQuoteElement = document.querySelector('.js-quote-request');
  if (requestQuoteElement) {
    requestQuoteElement.addEventListener('click', function() {
      window.sessionStorage.removeItem('requestInfoData');
    });
  }
};

const setupAnalyticsListener = function() {
  const navLinks = document.querySelectorAll('.main-nav a[data-nav-label]');

  navLinks.forEach(link => {
    link.addEventListener('click', function(event) {
      const label = link.getAttribute('data-nav-label');
      const placement = link.closest('.secondary-panel')
        ? secondaryPlacement
        : headerPlacement;
      trackNavClick(event, link, label, placement);
    });
  });

  // The account links arrive from the cart after load and are swapped on
  // sign-in, and spree/dom.js toggles the menu on its own element, so both
  // are read once the click reaches the document.
  document.addEventListener('click', function(event) {
    const toggle = event.target.closest('.js-account-links-toggle');
    if (!toggle) return;

    const link = event.target.closest('.js-account-links a');
    if (link) {
      trackNavClick(event, link, link.textContent.trim(), headerPlacement);
    } else if (toggle.querySelector('.js-account-links.show')) {
      sendInpEvent('Open Account Menu');
    }
  });

  function trackNavClick(event, link, label, placement) {
    const href = link.getAttribute('href');
    const useNewTab = link.target === '_blank';

    sendNavEvent(label, placement, link.href);
    // Modified clicks open a new tab or window natively, leaving this page.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    // Override default navigation
    event.preventDefault();
    setTimeout(() => {
      if (useNewTab) {
        window.open(href, '_blank');
      } else {
        window.location.href = href;
      }
    }, 100); // Slight delay for tracking to go through
  }

  function sendNavEvent(label, placement, url) {
    let navType = label.toLowerCase().includes('lightart')
      ? 'EXIT'
      : label.toLowerCase().includes('find a consultant')
      ? 'INP'
      : 'NAV';
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `${navType}|${placement}|${label}`,
      },
    };
    // Launch reports an exit link's destination from eVar108.
    if (navType === 'EXIT') eventData.detail.eVar108 = url;
    fireAdobeAnalyticsEvent(eventData);
  }
};

const sendInpEvent = function(name) {
  const eventData = {
    type: 'click',
    detail: {
      eVar8: `INP|${headerPlacement}|${name}`,
    },
  };
  fireAdobeAnalyticsEvent(eventData);
};

const toggleNavPanelDisplay = function() {
  if (globalSearch.classList.contains(showClass)) {
    toggleSearchPanelDisplay();
  }

  const nowOpen = navPanel.classList.toggle(showClass);
  syncSharedClasses();
  mobileNavToggle.setAttribute('aria-expanded', String(nowOpen));

  if (nowOpen) {
    document.addEventListener('keyup', escapeKeyNavClose);
  } else {
    document.removeEventListener('keyup', escapeKeyNavClose);
    // Blur inputs so iOS Safari can zoom the viewport back out on close.
    // querySelector can't cross shadow roots, so reach in explicitly.
    navPanel.shadowRoot?.querySelector('global-search')?.blur();
    globalSearch.blur();
  }
};

const toggleSearchPanelDisplay = function(event) {
  if (event) event.preventDefault();

  if (isMobileOrTablet()) {
    // On mobile/tablet the search input lives inside the nav-panel's first
    // mobile view; reset to that view before opening so it's visible.
    const opening = !navPanel.classList.contains(showClass);
    if (opening) navPanel.dispatchEvent(resetMobileNav);

    toggleNavPanelDisplay();

    if (opening) {
      navPanel.updateComplete?.then(() => {
        navPanel.shadowRoot?.querySelector('global-search')?.focus();
      });
    }
    return;
  }

  if (navPanel.classList.contains(showClass)) {
    toggleNavPanelDisplay();
  }

  const nowOpen = globalSearch.classList.toggle(showClass);
  syncSharedClasses();
  globalSearchTrigger.setAttribute('aria-expanded', String(nowOpen));
  if (nowOpen) globalSearch.focus();
};

// Below desktop, lock scroll on <html> instead of taking main and the footer
// out of flow: shrinking the page under one screen makes iOS stop drawing the
// fixed nav-panel in a freshly opened tab.
const syncSharedClasses = function() {
  const open =
    navPanel.classList.contains(showClass) ||
    globalSearch.classList.contains(showClass);
  const mobile = isMobileOrTablet();

  document.documentElement.classList.toggle(lockScrollClass, open && mobile);
  main.classList.toggle('no-scroll', open && !mobile);
  footer.classList.toggle('hidden', open && !mobile);
  main.classList.toggle(dimmedClass, open && !mobile);
};

const escapeKeyNavClose = function(event) {
  if (event.key === 'Escape') {
    toggleNavPanelDisplay();
  }
};

const parentListItemEventListeners = function() {
  parentListItems.forEach(parentListItem => {
    parentListItem
      .querySelector('a')
      .addEventListener('click', function(_event) {
        if (
          // prettier-ignore
          (
            navPanel.getAttribute('currentpanel') ===
              parentListItem.getAttribute('data-content') &&
            navPanel.classList.contains(showClass)
          ) ||
          !navPanel.classList.contains(showClass)
        ) {
          toggleNavPanelDisplay();
        }

        navPanel.setAttribute(
          'currentpanel',
          parentListItem.getAttribute('data-content')
        );
        navPanel.dispatchEvent(navSelectedEvent);
        parentListItem.classList.add('is-active');
        navPanel.scrollTop = 0;
        setTabIndexes(
          parentListItem.querySelectorAll('.js-child-list-item'),
          '0'
        );
        // A second click on the open item closes it; only opens report.
        if (navPanel.classList.contains(showClass)) {
          sendInpEvent(`Open ${parentListItem.getAttribute('data-content')}`);
        }
      });
  });
};

export default {
  init,
};
